import { useCallback, useState } from "react"

import AddAdjustmentForm from "./AddAdjustmentForm"
import styles from "./AdjustmentSubTab.module.scss"
import AdjustmentTable from "./AdjustmentTable"
import YearMonthSelector from "./YearMonthSelector"

import { graphql } from "@/company_admin/gql/__generated__"
import type { UInt } from "@/company_admin/gql/__generated__/graphql"
import type { AddAdjustmentFormData } from "@/company_admin/utils/forms/adjustment"
import {
	formatYearMonth,
	getCurrentYearMonth,
} from "@/company_admin/utils/yearMonth"
import {
	useCompanyAdminMutation,
	useCompanyAdminQuery,
} from "@/provider/apollo/Apollo"

const GetAdjustmentsByYearMonth = graphql(/* GraphQL */ `
	query GetAdjustmentsByYearMonth($param: AdjustmentsByYearMonthParam!) {
		adjustmentsByYearMonth(param: $param) {
			adjustments {
				...AdjustmentFields
			}
			total {
				...AccountingAmountFields
			}
		}
	}
`)

const EditYearMonthAdjustments = graphql(/* GraphQL */ `
	mutation EditYearMonthAdjustments($input: EditYearMonthAdjustmentsInput!) {
		editYearMonthAdjustments(input: $input) {
			dummy
		}
	}
`)

type AdjustmentSubTabProps = {
	accountingGroupId: string
	isSouhlAdmin: boolean
	refetchSettlements: () => void
}

const AdjustmentSubTab = ({
	accountingGroupId,
	isSouhlAdmin,
	refetchSettlements,
}: AdjustmentSubTabProps) => {
	const currentYm = formatYearMonth(getCurrentYearMonth(new Date()))
	const [selectedYearMonth, setSelectedYearMonth] = useState(currentYm)
	const [showAddForm, setShowAddForm] = useState(false)

	// 調整一覧クエリ
	const { data, refetch } = useCompanyAdminQuery(GetAdjustmentsByYearMonth, {
		variables: {
			param: {
				accountingGroupId,
				yearMonth: selectedYearMonth,
			},
		},
	})

	// 追加用 mutation
	const [addMutate, { loading: addLoading }] = useCompanyAdminMutation(
		EditYearMonthAdjustments
	)

	// 削除用 mutation
	const [deleteMutate, { loading: deleteLoading }] = useCompanyAdminMutation(
		EditYearMonthAdjustments
	)

	const handleYearMonthChange = useCallback((ym: string) => {
		setSelectedYearMonth(ym)
		setShowAddForm(false)
	}, [])

	const handleAdd = useCallback(
		async (formData: AddAdjustmentFormData) => {
			await addMutate({
				variables: {
					input: {
						accountingGroupId,
						yearMonth: selectedYearMonth,
						additions: [
							{
								amount: formData.amount as UInt,
								isAmountKindPayout: formData.isAmountKindPayout,
								name: formData.name,
								memo: formData.memo || null,
							},
						],
						deletionIds: [],
					},
				},
			})
			setShowAddForm(false)
			await refetch()
			refetchSettlements()
		},
		[
			addMutate,
			accountingGroupId,
			selectedYearMonth,
			refetch,
			refetchSettlements,
		]
	)

	const handleDelete = useCallback(
		async (adjustmentId: string) => {
			if (
				!window.confirm(
					"この調整を削除しますか？削除すると Settlement が再作成されます。"
				)
			) {
				return
			}
			await deleteMutate({
				variables: {
					input: {
						accountingGroupId,
						yearMonth: selectedYearMonth,
						additions: [],
						deletionIds: [adjustmentId],
					},
				},
			})
			await refetch()
			refetchSettlements()
		},
		[
			deleteMutate,
			accountingGroupId,
			selectedYearMonth,
			refetch,
			refetchSettlements,
		]
	)

	const adjustmentsData = data?.adjustmentsByYearMonth

	return (
		<div className={styles.adjustment_subtab}>
			<div className={styles.header}>
				<YearMonthSelector
					selectedYearMonth={selectedYearMonth}
					onSelectYearMonth={handleYearMonthChange}
				/>
				{isSouhlAdmin && !showAddForm && (
					<button
						type="button"
						className={styles.add_button}
						onClick={() => setShowAddForm(true)}
					>
						調整を追加
					</button>
				)}
			</div>

			{adjustmentsData && (
				<AdjustmentTable
					adjustments={adjustmentsData.adjustments}
					total={adjustmentsData.total}
					isSouhlAdmin={isSouhlAdmin}
					onDelete={handleDelete}
					isDeleting={deleteLoading}
				/>
			)}

			{isSouhlAdmin && showAddForm && (
				<AddAdjustmentForm
					onSubmit={handleAdd}
					onCancel={() => setShowAddForm(false)}
					isSubmitting={addLoading}
				/>
			)}
		</div>
	)
}

export default AdjustmentSubTab
